import type { ApiCondo } from './api/ApiCondo';
import type { ApiProperty } from './api/ApiProperty';
import type { CondoFeatureCategory, CondoFeatureEnum } from './CRMCondoEnums';
import type { FeatureObject } from './CRMEnums';
import type { CondoSystemEnums, SystemEnums } from './SystemEnums';

import { FeatureCategory, FeatureEnum } from './CRMEnums';

/**
 * Creates a `FeatureObject` based on a feature string and the system enums.
 *
 * @param feature - A single feature string.
 * @param enums - The system enums used to retrieve the feature's category and name.
 * @returns A `FeatureObject` if the feature exists in the system enums, otherwise `null`.
 */
export function createFeatureObject(
	feature: FeatureEnum | CondoFeatureEnum | null,
	enums: SystemEnums | CondoSystemEnums
): FeatureObject | null {
	if (!feature) return null;

	const formattedFeature = feature?.toLowerCase().trim() as
		| FeatureEnum
		| CondoFeatureEnum;

	const featureObject = enums.features[formattedFeature] as FeatureObject;
	if (!featureObject) return null;

	return {
		name: featureObject.name,
		identifier: featureObject.identifier,
		category: featureObject.category
	};
}

/**
 * Maps a list of feature strings to an array of `FeatureObject`.
 *
 * @param features - A list of feature strings from the API.
 * @param enums - The system enums used to map features to categories.
 * @returns An array of `FeatureObject` containing the mapped feature data.
 */
export function mapFeatures(
	features: (ApiCondo | ApiProperty)['features'],
	enums: SystemEnums | CondoSystemEnums
): FeatureObject[] {
	return (features ?? [])
		.map(feature => createFeatureObject(feature, enums))
		.filter(Boolean) as FeatureObject[];
}

/**
 * Groups features by their category names.
 *
 * @param features - An array of `FeatureObject`.
 * @returns An object where the keys are category names, and the values are arrays of `FeatureObject`.
 */
export function groupFeaturesByCategory(
	features: FeatureObject[],
	featureCategoriesTranslations:
		| Record<FeatureCategory, string>
		| Record<CondoFeatureCategory, string>
): Record<string, FeatureObject[]> {
	return features.reduce(
		(acc, feature) => {
			const categoryName = featureCategoriesTranslations[feature.category];
			const currentCategoryFeatures = acc[categoryName] || [];
			return {
				...acc,
				[categoryName]: [...currentCategoryFeatures, feature]
			};
		},
		{} as Record<string, FeatureObject[]>
	);
}
